import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import colour from 'client/utils/constants/GDS/js/color';

const defaultSize = 30;

const PolygonIcon = ({ fill, points, stroke, imageSource, ...props }) => {
    const canvasRef = useRef();

    useEffect(() => {
        updateCanvas();
    }, [fill, points, stroke, imageSource]);

    const updateCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) {
            return;
        }

        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        // Reset canvas when updating, otherwise when updating imageSource old image remains
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (imageSource) {
            const img = new Image();
            img.src = imageSource.toString();
            img.onload = () => {
                ctx.fillStyle = ctx.createPattern(img, 'repeat');
                ctx.fillRect(defaultSize / 8, defaultSize / 4, defaultSize * 0.75, defaultSize * 0.5);
            };
        }

        if (!imageSource && fill?.color) {
            ctx.fillStyle = fill?.color;
            ctx.fillRect(defaultSize / 8, defaultSize / 4, defaultSize * 0.75, defaultSize * 0.5);
        }

        ctx.strokeStyle = stroke?.color;
        ctx.lineCap = stroke?.lineCap;
        ctx.lineJoin = stroke?.lineJoin;
        if (stroke?.lineDash) {
            ctx.setLineDash(stroke?.lineDash);
        }
        ctx.setMiterLimit = stroke?.miterLimit;
        ctx.lineDashOffset = stroke?.lineDashOffset;
        ctx.lineWidth = stroke?.width;
        ctx.strokeRect(defaultSize / 8, defaultSize / 4, defaultSize * 0.75, defaultSize * 0.5);
    };

    return (
        <canvas
            ref={canvasRef}
            width={defaultSize}
            height={defaultSize}
            data-testid={props['data-testid']}
        />
    );
};

PolygonIcon.propTypes = {
    imageSource: PropTypes.instanceOf(URL),
    fill: PropTypes.shape({
        color: PropTypes.string
    }),
    points: PropTypes.number,
    stroke: PropTypes.shape({
        color: PropTypes.string,
        lineCap: PropTypes.oneOf(['butt', 'round', 'square']),
        lineJoin: PropTypes.oneOf(['bevel', 'round', 'miter']),
        lineDash: PropTypes.arrayOf(PropTypes.number),
        lineDashOffset: PropTypes.number,
        miterLimit: PropTypes.number,
        width: PropTypes.number
    }),
    'data-testid': PropTypes.string
};

PolygonIcon.defaultProps = {
    fill: {
        color: colour.black
    },
    points: 4,
    radius: 10,
    angle: 0,
    stroke: {
        color: colour.black,
        lineCap: 'round',
        lineJoin: 'round',
        lineDashOffset: 0,
        miterLimit: 10,
        width: 3
    },
    displacement: [0, 0],
    rotation: 0
};

export default PolygonIcon;
